// Sanitização leve no cliente (defesa em profundidade — backend também sanitiza)
const PATTERNS: RegExp[] = [
  /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
  /\b(?:\+?244\s?)?9\d{2}[\s-]?\d{3}[\s-]?\d{3}\b/g,
  /\bAO\d{2}(?:[\s-]?\d){21}\b/gi,
  /\b(?:\d[ -]*?){13,19}\b/g,
];

export function containsSensitiveData(text: string): boolean {
  return PATTERNS.some((re) => re.test(text));
}

export function maskSensitive(text: string): string {
  let out = text;
  for (const re of PATTERNS) out = out.replace(re, "[oculto]");
  return out;
}

function stripAccents(str: string): string {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function hasObfuscatedWord(text: string, word: string): boolean {
  if (word.length === 0) return false;
  // Construct regex: \bw\s*o\s*r\s*d\b with optional separators in between characters
  const chars = word.split("").map((c, i) => i === word.length - 1 ? c : `${c}[\\s.\\-_]*`).join("");
  const regex = new RegExp(`\\b${chars}\\b`, "i");
  return regex.test(text);
}

// 1. Off-platform payment patterns
const offPlatformPatterns = [
  /paga(?:r|mento)?\s+(?:por\s+)?fora\s+(?:da\s+)?plataforma/i,
  /negocia(?:r)?\s+(?:por\s+)?fora\s+(?:da\s+)?plataforma/i,
  /fora\s+(?:da\s+)?plataforma/i,
  /\bpaga\s+(?:comigo|aqui|depois|por\s+fora)\b/i,
  /\bpaga(?:r|mento)?\s+por\s+fora\b/i,
  /\bpaga(?:r)?\s+quando\s+(?:chegar(?:es)?|vier(?:es)?)\b/i,
  /\bpaga(?:r)?\s+quando\s+chegar(?:es)?\s+(?:ao\s+espaco|no\s+local)\b/i,
  /\baqui\s+fica\s+caro\b/i,
  /\besta\s+caro\s+aqui\b/i,
  /\bfica\s+mais\s+barato\s+fora\b/i,
  /\bmais\s+barato\b/i,
  /\bdesconto\s+fora\b/i,
  /\bvou\s+(?:te\s+)?fazer\s+desconto\b/i,
  /\bfa[cç]o\s+desconto\b/i,
  /\bdesconto\s+se\s+pagar(?:es)?\s+fora\b/i,
  /\bpodes\s+vir\s+agora\b/i,
];

// 2. Bank transfer & account details
const bankTransferPatterns = [
  /envia(?:r)?\s+(?:o\s+)?dinheiro/i,
  /manda(?:r)?\s+(?:o\s+)?dinheiro/i,
  /manda(?:r)?\s+(?:para\s+)?(?:a\s+)?(?:minha\s+)?conta/i,
  /transferencia\s+bancaria/i,
  /transfere\s+(?:para\s+)?mim/i,
  /\bmeu\s+iban\b/i,
  /\biban\b/i,
  /\bconta\s+bancaria\b/i,
  /\bminha\s+conta\s+bancaria\b/i,
  /numero\s+(?:da\s+)?conta/i,
  /dados\s+bancarios/i,
];

// 3. Contact sharing patterns
const contactSharingPatterns = [
  /meu\s+(?:numero|contacto|telefone|wpp|whats|whatsapp|zap)\b/i,
  /manda\s+(?:teu|o)\s+(?:numero|contacto|telefone|wpp|whats|whatsapp|zap)\b/i,
  /contacto\s+direto/i,
  /fala(?:r)?\s+comigo\s+fora/i,
  /liga(?:r)?\s+para\s+mim/i,
  /chama(?:r)?\s+fora\s+(?:da\s+)?plataforma/i,
  /vamos\s+(?:no|conversar\s+no|falar\s+no)\s+(?:whatsapp|whatsap|watsap|wpp|zap)/i,
];

// 4. Trust / bypass patterns
const trustPatterns = [
  /pode(?:s)?\s+confiar\b/i,
  /confia\s+em\s+mim\b/i,
  /sou\s+de\s+confian[cç]a\b/i,
  /n[aã]o\s+te\s+preocupes\b/i,
  /fa[cç]o\s+mais\s+barato\b/i,
  /vem\s+direto\b/i,
];

// 5. Abusive patterns
const abusivePatterns = [
  /\b(?:burro|otario|estupido|filho\s+da\s+puta|fdp|burlador|ladrao|ameaca|violencia|agressao|insulto|estupida|imbecil|canalha|corrupto|vigarista|golpista)\b/i
];

export function analyzeMessageForSafety(text: string): { isSafe: boolean; reason?: string } {
  const normalized = stripAccents(text).toLowerCase();
  const unsafeReason = "Esta mensagem não pode ser enviada porque viola as regras de segurança da BeraStays. Para sua proteção, pagamentos, contactos e negociações devem acontecer dentro da plataforma.";

  // 1. Obfuscated keywords check
  const obfuscatedKeywords = [
    "iban",
    "whatsapp",
    "whatsap",
    "watsap",
    "wpp",
    "zap",
    "paypal",
    "binance",
    "bitcoin",
    "usdt",
    "ethereum",
    "telegram",
    "instagram",
    "multicaixa",
    "link",
    "cripto",
    "crypto"
  ];

  for (const keyword of obfuscatedKeywords) {
    if (hasObfuscatedWord(normalized, keyword)) {
      return { isSafe: false, reason: unsafeReason };
    }
  }

  // 2. Off-platform payment patterns
  if (offPlatformPatterns.some(p => p.test(normalized))) {
    return { isSafe: false, reason: unsafeReason };
  }

  // 3. Bank transfer & account details
  if (bankTransferPatterns.some(p => p.test(normalized))) {
    return { isSafe: false, reason: unsafeReason };
  }

  // 4. Contact sharing patterns
  if (contactSharingPatterns.some(p => p.test(normalized))) {
    return { isSafe: false, reason: unsafeReason };
  }

  // 5. Trust / bypass patterns
  if (trustPatterns.some(p => p.test(normalized))) {
    return { isSafe: false, reason: unsafeReason };
  }

  // 6. Abusive patterns
  if (abusivePatterns.some(p => p.test(normalized))) {
    return { isSafe: false, reason: unsafeReason };
  }

  // 7. Allowed links check (external links blocked except BeraStays domains)
  const linkRegex = /(?:https?:\/\/|www\.)[a-z0-9\-_.]+\.[a-z]{2,6}|\b[a-z0-9\-_.]+\.(?:com|ao|pt|net|org|xyz|app|link|io|co|info|me|cc|online|site|club|live|ly)\b/gi;
  const foundLinks = normalized.match(linkRegex);
  if (foundLinks) {
    for (const link of foundLinks) {
      const isAllowed = link.includes("berastays.com") || link.includes("bera-stays.lovable.app") || link.includes("localhost");
      if (!isAllowed) {
        return { isSafe: false, reason: unsafeReason };
      }
    }
  }

  // 8. Contact numbers or IBAN digit extraction check
  const digits = normalized.replace(/\D/g, "");
  if (/9[0-9]{8}/.test(digits) || /2449[0-9]{8}/.test(digits)) {
    return { isSafe: false, reason: unsafeReason };
  }
  if (/[0-9]{13,}/.test(digits)) {
    return { isSafe: false, reason: unsafeReason };
  }

  // 9. Standard regexes fallback
  if (containsSensitiveData(text)) {
    return { isSafe: false, reason: unsafeReason };
  }

  return { isSafe: true };
}
